import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.error || error.message;
    console.error('API Error:', message);

    // On 401, clear stored credentials and redirect to login
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// ============================================
// Auth API
// ============================================
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// ============================================
// Dashboard API
// ============================================
export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
  getCategoryHours: (periodId, employeeId) => {
    const p = new URLSearchParams();
    if (periodId)   p.set('periodId',   periodId);
    if (employeeId) p.set('employeeId', employeeId);
    return api.get(`/dashboard/category-hours?${p}`);
  },
};

// ============================================
// Employees API
// ============================================
export const employeesAPI = {
  getAll: (activeOnly = true) => api.get(`/employees?activeOnly=${activeOnly}`),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  deactivate: (id) => api.post(`/employees/${id}/deactivate`),
  activate: (id) => api.post(`/employees/${id}/activate`),
  getTimesheets: (id) => api.get(`/employees/${id}/timesheets`),
  getPayslips: (id) => api.get(`/employees/${id}/payslips`),
  getPortalAccount: (id) => api.get(`/employees/${id}/portal-account`),
  createPortalAccount: (id) => api.post(`/employees/${id}/create-portal-account`),
  revokeAccess: (id) => api.post(`/employees/${id}/revoke-access`),
  restoreAccess: (id) => api.post(`/employees/${id}/restore-access`),
  resetPassword: (id) => api.post(`/employees/${id}/reset-password`),
  bulkUpload: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/employees/bulk', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ============================================
// Timesheet API
// ============================================
export const timesheetAPI = {
  process: (periodId) => api.post('/timesheet/process', { periodId }),
  generate: (employeeId, periodId) => api.post('/timesheet/generate', { employeeId, periodId }),
  getPending: () => api.get('/timesheet/pending'),
  getRejected: () => api.get('/timesheet/rejected'),
  getCounts: () => api.get('/timesheet/counts'),
  getPeriods: (limit, offset, type) => {
    const p = new URLSearchParams({ limit: limit || 20, offset: offset || 0 });
    if (type) p.set('type', type);
    return api.get(`/timesheet/periods?${p}`);
  },
  getPeriod: (id) => api.get(`/timesheet/periods/${id}`),
  createPeriod: (data) => api.post('/timesheet/periods', data),
  updatePeriod: (id, data) => api.put(`/timesheet/periods/${id}`, data),
  deletePeriod: (id) => api.delete(`/timesheet/periods/${id}`),
  // Super admin only — lifts/restores the lock on a processed period.
  unlockPeriod: (id) => api.post(`/timesheet/periods/${id}/unlock`),
  lockPeriod:   (id) => api.post(`/timesheet/periods/${id}/lock`),
  releasePayslips: (periodId) => api.post(`/timesheet/periods/${periodId}/release-payslips`),
  markBankDownloaded: (periodId, type) => api.post(`/timesheet/periods/${periodId}/mark-bank-downloaded?type=${type}`),
  markBankUploaded: (periodId) => api.post(`/timesheet/periods/${periodId}/mark-bank-uploaded`),
  createMonthlyPeriods: (year, month) => api.post('/timesheet/periods/monthly', { year, month }),
  createForeignMonthlyPeriod: (year, month) => api.post('/timesheet/periods/foreign-monthly', { year, month }),
  getPeriodSummaries: (id) => api.get(`/timesheet/periods/${id}/summaries`),
  getPeriodPayslips: (id) => api.get(`/timesheet/periods/${id}/payslips`),
  downloadSummaryPDF: async (periodId, periodName) => {
    const res = await api.get(`/timesheet/periods/${periodId}/summary-pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `PayslipSummary_${(periodName || periodId).replace(/[^a-zA-Z0-9-]/g, '_')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
  // A failed blob request carries its JSON error body inside a Blob, so the
  // usual err.response.data.error is undefined — dig it back out.
  downloadRangeSummary: async (startDate, endDate, format = 'pdf') => {
    const qs = new URLSearchParams({ start: startDate, end: endDate, format });
    let res;
    try {
      res = await api.get(`/payroll/summary-range?${qs}`, { responseType: 'blob' });
    } catch (err) {
      const body = err.response?.data;
      if (body instanceof Blob) {
        try {
          const parsed = JSON.parse(await body.text());
          if (parsed?.error) throw new Error(parsed.error);
        } catch (parseErr) {
          if (parseErr instanceof Error && parseErr.message) throw parseErr;
        }
      }
      throw err;
    }
    const mime = format === 'csv' ? 'text/csv;charset=utf-8' : 'application/pdf';
    const url = URL.createObjectURL(new Blob([res], { type: mime }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `PayrollSummary_${startDate}_to_${endDate}.${format}`;
    a.click();
    URL.revokeObjectURL(url);
  },
  downloadSummaryXLSX: async (periodId, periodName) => {
    const res = await api.get(`/timesheet/periods/${periodId}/summary-xlsx`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `PayslipSummary_${(periodName || periodId).replace(/[^a-zA-Z0-9-]/g, '_')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },
  getSummary: (id) => api.get(`/timesheet/summaries/${id}`),
  resendApproval: (id) => api.post(`/timesheet/summaries/${id}/resend`),
  approveSummary: (id) => api.post(`/timesheet/summaries/${id}/approve`),
  rejectSummary: (id, reason) => api.post(`/timesheet/summaries/${id}/reject`, { reason }),
  generatePayslip: (id) => api.post(`/timesheet/summaries/${id}/generate-payslip`),
  getEmployeeEntries: (id, startDate, endDate) => 
    api.get(`/timesheet/employees/${id}/entries?startDate=${startDate}&endDate=${endDate}`),
  addEntry: (data) => api.post('/timesheet/entries', data),
  bulkImportEntries: (entries) => api.post('/timesheet/entries/bulk', { entries }),
};

// ============================================
// Payslips API
// ============================================
export const payslipsAPI = {
  getById: (id) => api.get(`/timesheet/payslips/${id}`),
  bulkGenerate: (periodId, employeeIds) =>
    api.post('/timesheet/bulk-generate-payslips', { periodId, employeeIds }),
  generateForPeriod: (periodId, employeeIds) =>
    api.post('/timesheet/generate-payslips-for-period', { periodId, employeeIds }),
  pdfUrl: (id) =>
    `${API_BASE_URL}/timesheet/payslips/${id}/pdf?token=${localStorage.getItem('token')}`,
  downloadPDF: (id) => api.get(`/timesheet/payslips/${id}/pdf`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/timesheet/payslips/${id}`),
  releaseOne: (id) => api.post(`/timesheet/payslips/${id}/release`),
  downloadBankFile: async (periodId, type, employeeIds = null, periodName = null) => {
    const params = new URLSearchParams({ periodId, type });
    if (employeeIds && employeeIds.length) params.set('employeeIds', employeeIds.join(','));
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/payroll/bank-file?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `Download failed (HTTP ${res.status})` }));
      const e = new Error(err.error || 'Download failed');
      e.code = err.code;
      e.missing = err.missing;
      throw e;
    }
    const safeName = periodName
      ? periodName.replace(/[()[\]]/g, '').trim().replace(/[^a-zA-Z0-9-]+/g, '_').replace(/_+$/g, '')
      : periodId;
    const typeLabel = type === 'foreign' ? 'Intl' : 'LCL';
    // The extension is not cosmetic — MBOS rejects the ISO 20022 EFT batch unless
    // it is a *.xls (Excel 97-2003) workbook, so follow whatever the server built.
    const serverName = res.headers.get('Content-Disposition') || '';
    // Both MBOS formats are Excel 97-2003 (*.xls); the server name wins when readable.
    const ext = (serverName.match(/filename="?[^"]*\.([A-Za-z0-9]+)"?/) || [])[1] || 'xls';
    const filename = `BT_${typeLabel}_${safeName}.${ext.toLowerCase()}`;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    // The server excludes employees it cannot build a valid row for — report them.
    return {
      filename,
      skippedCount: parseInt(res.headers.get('X-Skipped-Count') || '0', 10),
      skippedNames: res.headers.get('X-Skipped-Names') || '',
    };
  },
};

// ============================================
// Timesheet Generator API
// ============================================
export const timesheetGeneratorAPI = {
  preview: (employeeId, startDate, endDate) =>
    api.post('/timesheet/preview', { employeeId, startDate, endDate }),
  submit: (employeeId, startDate, endDate, periodName) =>
    api.post('/timesheet/submit', { employeeId, startDate, endDate, periodName }),
};

// ============================================
// Jobs API (async payroll job polling)
// ============================================
export const jobsAPI = {
  getStatus: (id) => api.get(`/jobs/${id}`),
};

// ============================================
// Wrike API
// ============================================
export const wrikeAPI = {
  getWeeklyTimelogs: (date, approvedOnly = false) =>
    api.get(`/wrike/timelogs${date ? `?date=${date}` : '?'}${approvedOnly ? '&approvedOnly=true' : ''}`),
  getMonthlyTimelogs: (month, approvedOnly = false) =>
    api.get(`/wrike/timelogs/monthly?month=${month}${approvedOnly ? '&approvedOnly=true' : ''}`),
  importWeek: (date, approvedOnly = false) => api.post('/wrike/import', { date, approvedOnly }),
  backfillCategories: () => api.post('/wrike/backfill-categories'),
  importPeriod: (periodId) => api.post('/wrike/import-period', { periodId }),
  getContacts: () => api.get('/wrike/contacts'),
};

// ============================================
// Timesheet Verifications API
// ============================================
export const verificationsAPI = {
  getForPeriod: (periodId) => api.get(`/verifications/period/${periodId}`),
  upsert: (data) => api.post('/verifications/upsert', data),
  bulk: (period_id, employee_ids, status) => api.post('/verifications/bulk', { period_id, employee_ids, status }),
  getStatus: (periodId, employeeIds) => {
    const p = new URLSearchParams({ periodId });
    if (employeeIds?.length) p.set('employeeIds', employeeIds.join(','));
    return api.get(`/verifications/status?${p}`);
  },
};

// ============================================
// Webhooks API
// ============================================
export const webhooksAPI = {
  getLogs: (limit) => api.get(`/webhooks/logs?limit=${limit || 50}`),
  getUnprocessed: () => api.get('/webhooks/unprocessed'),
  retry: () => api.post('/webhooks/retry'),
  test: (taskId, status) => api.post('/webhooks/test', { taskId, status }),
};

// ============================================
// Employee Portal API
// ============================================
export const portalAPI = {
  getMe: () => api.get('/portal/me'),
  getCategoryHours: (periodId) => {
    const p = new URLSearchParams();
    if (periodId) p.set('periodId', periodId);
    return api.get(`/portal/category-hours?${p}`);
  },
  updateProfile: (data) => api.put('/portal/profile', data),
  getTimesheets: () => api.get('/portal/timesheets'),
  getTimesheetDetail: (id) => api.get(`/portal/timesheets/${id}`),
  approve: (id) => api.post(`/portal/timesheets/${id}/approve`),
  reject: (id, reason, files) => {
    const form = new FormData();
    form.append('reason', reason);
    if (files) files.forEach(f => form.append('files', f));
    return api.post(`/portal/timesheets/${id}/reject`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadPDF: (id) => api.get(`/portal/timesheets/${id}/pdf`, { responseType: 'blob' }),
  getPayslips: () => api.get('/portal/payslips'),
  downloadPayslipPDF: (id) => api.get(`/portal/payslips/${id}/pdf`, { responseType: 'blob' }),
  payslipPdfUrl: (id) => `${API_BASE_URL}/portal/payslips/${id}/pdf?token=${localStorage.getItem('token')}`,
  changePassword: (currentPassword, newPassword) =>
    api.post('/portal/change-password', { currentPassword, newPassword }),
};

export const usersAPI = {
  getAll:        ()         => api.get('/admin/users'),
  create:        (data)     => api.post('/admin/users', data),
  update:        (id, data) => api.put(`/admin/users/${id}`, data),
  resetPassword: (id, pwd)  => api.post(`/admin/users/${id}/reset-password`, { password: pwd }),
  deactivate:    (id)       => api.post(`/admin/users/${id}/deactivate`),
  activate:      (id)       => api.post(`/admin/users/${id}/activate`),
  delete:        (id)       => api.delete(`/admin/users/${id}`),
  permissionCatalog: ()     => api.get('/admin/permissions/catalog'),
  // Pass { reset: true } to drop the override and fall back to role defaults.
  updatePermissions: (id, body) => api.put(`/admin/users/${id}/permissions`, body),
};

export default api;
